/**
 * Kiosk session keeper.
 *
 * A gate-mounted attendance kiosk must behave like a POS terminal: once an
 * operator activates it, it stays signed in **until someone explicitly signs
 * out** — never bouncing to /login mid-day because an access/refresh token
 * quietly expired (which is exactly what happens if the tablet sits offline for
 * days or reboots after being powered down for weeks).
 *
 * To guarantee that, we persist the activation credentials in the platform's
 * secure store (Keychain / EncryptedSharedPreferences on native, localStorage
 * on web — see secure-storage.ts) and transparently re-login when the refresh
 * token is dead. This is a deliberate kiosk trade-off: the device is physically
 * controlled by the school and should run a dedicated KIOSK account (no 2FA).
 * Credentials are cleared the moment an operator taps "Sign out".
 *
 * No credentials are ever stored for a normal app login — only when a session
 * is activated through the kiosk gate (KioskAuthGate calls rememberKioskSession).
 */
import axios, { type AxiosResponse } from "axios";
import { getApiOrigin, isNative } from "@/lib/platform";
import { secureStorage, secureStorageSyncRead, secureStorageSyncWrite } from "@/lib/secure-storage";
import {
  clearLoginLock,
  loginLockIdentifier,
  loginLockSecondsLeft,
  readLoginLock,
  recordLoginLockVerdict,
} from "@/lib/login-lockout";
import { useAuthStore, type AuthUser } from "@/store/auth-store";

const CRED_KEY = "kiosk-cred";

interface KioskCredentials {
  email: string;
  password: string;
}

// Recompute the API base here rather than importing it from api/client to
// avoid an import cycle (client.ts depends on this module for its auth-failure
// handling). Mirrors the resolution in api/client.ts exactly.
function loginUrl(): string {
  const origin = getApiOrigin();
  const base = !origin
    ? "/api/v1"
    : origin.endsWith("/api")
      ? `${origin}/v1`
      : `${origin}/api/v1`;
  return `${base}/auth/login`;
}

// CAMP-2026-0327 — "kiosk frequently signs off unexpectedly".
//
// `hasKioskSession()` is the single switch that decides whether a 401 is
// answered with a transparent re-login (kiosk) or with logout + a hard bounce
// to /login (everyone else). It has to be synchronous — it runs inside the
// axios interceptor — so it can only read the *localStorage mirror*, never the
// Keychain / EncryptedSharedPreferences copy that is canonical on native.
//
// That mirror is volatile: an Android WebView evicts localStorage under storage
// pressure, "Clear cache" wipes it, and some app updates drop it — exactly the
// failure `restoreNativeSessionMirror()` already handles for the auth blob, but
// nothing handled for the kiosk credential. Once the mirror went, a gate tablet
// that had been running for days answered its next 401 by signing itself out and
// showing the activation screen, mid-day, with taps still queued.
//
// Two belts:
//   1. `activated` — an in-process latch. Once we have seen credentials in this
//      run, a later mirror eviction can no longer un-kiosk the device. Only an
//      explicit `forgetKioskSession()` (operator taps Sign out) clears it.
//   2. `restoreKioskCredMirror()` — on native cold start, copy the secure copy
//      back into the mirror before the app can issue its first request.
let activated = false;

/** True if this device has been activated as a kiosk (sync — safe in interceptors). */
export function hasKioskSession(): boolean {
  if (activated) return true;
  if (secureStorageSyncRead(CRED_KEY)) {
    activated = true;
    return true;
  }
  return false;
}

export async function rememberKioskSession(email: string, password: string): Promise<void> {
  await secureStorage.set(CRED_KEY, JSON.stringify({ email: email.trim().toLowerCase(), password }));
  activated = true;
}

export async function forgetKioskSession(): Promise<void> {
  activated = false;
  await secureStorage.remove(CRED_KEY);
}

/**
 * Re-seed the kiosk-credential mirror from the secure store on native cold
 * start. Mirrors `restoreNativeSessionMirror()` in auth-store, and must be
 * awaited (or raced with the mount deadline) by the entry point so the first
 * 401 of the session already sees `hasKioskSession() === true`.
 */
export async function restoreKioskCredMirror(): Promise<void> {
  if (!isNative()) return;
  try {
    if (secureStorageSyncRead(CRED_KEY)) {
      activated = true;
      return; // mirror is intact
    }
    const secure = await secureStorage.get(CRED_KEY);
    if (!secure) return;
    // Re-writes both the secure copy and the mirror, and sets the latch.
    secureStorageSyncWrite(CRED_KEY, secure);
    activated = true;
    console.info("[kiosk] restored credential mirror from the secure store");
  } catch {
    // Best-effort. Worst case we fall back to the pre-existing behaviour.
  }
}

async function getKioskCredentials(): Promise<KioskCredentials | null> {
  const raw = await secureStorage.get(CRED_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as KioskCredentials;
  } catch {
    return null;
  }
}

interface LoginData {
  user?: AuthUser;
  accessToken?: string;
  refreshToken?: string;
  requiresOtp?: boolean;
}

// Coalesce concurrent re-logins onto one round-trip, same pattern as
// refreshAccessToken() in api/client.ts — a burst of 401s must not fire N
// parallel logins.
let reloginPromise: Promise<string> | null = null;

/**
 * Transparently re-establish the kiosk session from stored credentials.
 * Resolves with a fresh access token; rejects if there are no stored creds,
 * the account needs OTP (can't auto-complete), the password changed, or the
 * network is down. Callers treat a rejection as "stay on the kiosk, keep
 * serving cache / queueing" — never as a reason to sign out.
 *
 * CAMP-2026-0949 — it also backs off while the account is locked. Five failed
 * sign-ins lock an identifier server-side for 5 minutes (429 ACCOUNT_LOCKED),
 * and the kiosk is a shared account: a tablet whose stored password went stale
 * answers every 401 with another login, so without a back-off it would keep the
 * KIOSK account locked for everyone, indefinitely. After an ACCOUNT_LOCKED (or a
 * Retry-After'd RATE_LIMITED) it sends NOTHING until `lockedUntil`, then tries
 * once more. The wait is kept in sessionStorage (lib/login-lockout.ts), so a
 * reload of the tablet does not forget it.
 */
export function reloginKiosk(): Promise<string> {
  if (!reloginPromise) {
    reloginPromise = (async () => {
      const creds = await getKioskCredentials();
      if (!creds) throw new Error("No kiosk credentials stored");

      const lockKey = loginLockIdentifier(creds.email);
      const lockedUntil = lockKey ? readLoginLock(lockKey) : null;
      if (lockedUntil !== null) {
        throw new Error(`Kiosk account is locked — not retrying for ${loginLockSecondsLeft(lockedUntil)}s`);
      }

      let res: AxiosResponse<{ success: boolean; data: LoginData }>;
      try {
        res = await axios.post<{ success: boolean; data: LoginData }>(
          loginUrl(),
          { email: creds.email, password: creds.password },
          { withCredentials: true, headers: { "X-Client": isNative() ? "native" : "web" } },
        );
      } catch (err) {
        // A lock verdict arms the back-off above; any other failure is just a failure.
        recordLoginLockVerdict(lockKey, err);
        throw err;
      }
      if (lockKey) clearLoginLock(lockKey);
      const data = res.data?.data;
      if (data?.requiresOtp) throw new Error("Kiosk account requires OTP — cannot auto-reauth");
      if (!data?.user || !data?.accessToken) throw new Error("Kiosk re-login failed");

      useAuthStore.getState().login(data.user, data.accessToken, data.refreshToken);
      return data.accessToken;
    })().finally(() => {
      reloginPromise = null;
    });
  }
  return reloginPromise;
}
